/**
 * FollowUpHistoryTab + FollowUpStatusKpi
 * KPI de envios (página) e tabela filtrada (aba Histórico).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Pagination from '../../../../components/shared/Pagination';
import { cn } from '@/lib/utils';
import FollowUpHistoryTable from './FollowUpHistoryTable';
import { FOLLOW_UP_HISTORY_STATUSES } from '../followUp.constants';
import type { FollowUpHistoryStatus, FollowUpHistoryCounts } from '../hooks/useFollowUpHistory';
import type { FollowUpJob } from '../types';

const PAGE_SIZE = 10;

export function FollowUpStatusKpi({
    counts,
    activeStatus,
    onSelect,
}: {
    counts: FollowUpHistoryCounts;
    activeStatus?: FollowUpHistoryStatus | null;
    onSelect: (status: FollowUpHistoryStatus) => void;
}) {
    return (
        <section aria-label="Resumo de envios" className="mb-s5">
            <ul className="grid grid-cols-2 gap-s3 md:grid-cols-4">
                {FOLLOW_UP_HISTORY_STATUSES.map((card) => {
                    const isActive = activeStatus === card.id;
                    return (
                        <li key={card.id}>
                            <button
                                type="button"
                                onClick={() => onSelect(card.id)}
                                aria-pressed={isActive}
                                className={cn(
                                    'relative min-h-11 w-full touch-manipulation overflow-hidden rounded-xl border bg-surface px-s4 py-s4 text-left transition-shadow hover:shadow-sm md:py-s5',
                                    isActive ? 'border-primary/40 shadow-sm' : 'border-border',
                                )}
                            >
                                <span className={cn('absolute inset-x-0 top-0 h-[3px] rounded-t-xl', card.accentClass)} aria-hidden />
                                <p className="mb-s2 text-xs font-semibold uppercase tracking-[0.08em] text-muted">
                                    {card.label}
                                </p>
                                <p
                                    className={cn(
                                        'font-mono text-2xl font-semibold leading-none tracking-tight tabular-nums',
                                        card.valueClass,
                                    )}
                                >
                                    {counts[card.id]}
                                </p>
                                <p className="mt-s1 text-xs text-muted">{card.subtitle}</p>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

interface FollowUpHistoryTabProps {
    status: FollowUpHistoryStatus;
    startDate: string;
    setStartDate: (value: string) => void;
    endDate: string;
    setEndDate: (value: string) => void;
    rows: FollowUpJob[];
    loading: boolean;
    refresh: () => void;
}

const FollowUpHistoryTab: React.FC<FollowUpHistoryTabProps> = ({
    status,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    rows,
    loading,
    refresh,
}) => {
    const [currentPage, setCurrentPage] = useState(1);

    const paginatedRows = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * PAGE_SIZE;
        return rows.slice(firstPageIndex, firstPageIndex + PAGE_SIZE);
    }, [currentPage, rows]);

    useEffect(() => {
        setCurrentPage(1);
    }, [status, startDate, endDate, rows.length]);

    return (
        <section className="flex flex-col gap-s4" aria-label="Histórico de follow-ups">
            <div className="flex flex-col gap-s3 sm:flex-row sm:flex-wrap sm:items-end">
                <Input
                    label="Data início"
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    containerClassName="min-w-0 sm:max-w-52"
                />
                <Input
                    label="Data final"
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    containerClassName="min-w-0 sm:max-w-52"
                />
                <Button
                    type="button"
                    onClick={refresh}
                    disabled={loading}
                    className="min-h-11 w-full shrink-0 touch-manipulation shadow-none sm:w-auto md:min-h-9"
                >
                    Atualizar
                </Button>
            </div>

            <div className="overflow-hidden md:rounded-xl md:border md:border-border md:bg-surface">
                <FollowUpHistoryTable status={status} rows={paginatedRows} loading={loading} />
                {!loading && rows.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalCount={rows.length}
                        pageSize={PAGE_SIZE}
                        onPageChange={setCurrentPage}
                        className="mt-s3 border-t border-border md:mt-0"
                    />
                )}
            </div>
        </section>
    );
};

export default FollowUpHistoryTab;
